import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { parivarApi } from '../api/parivars';

export const PARIVAR_STORAGE_KEY = 'avdhoot_selected_parivar';
export const PARIVAR_CHOSEN_KEY = 'avdhoot_parivar_chosen';

export const DEFAULT_PARIVAR_SLUG = 'sarkhej';

const ParivarContext = createContext({
  availableParivars: [],
  selectedParivar: null,
  ready: false,
  needsOnboarding: false,
  changeParivar: () => {},
  markExplicitChoice: () => {},
});

/**
 * Shared Parivar state for the whole app. Loads the active Parivars once,
 * honours the visitor's stored choice, and falls back to Sarkhej (then the
 * first active Parivar) whenever the stored id is missing, invalid or stale.
 *
 * A visitor is only considered to have made a Parivar choice when they
 * explicitly picked one (the navbar dropdown or the first-visit modal). The
 * fallback default never counts, so the first-visit modal can keep nagging
 * until a real choice exists.
 */
export function ParivarProvider({ children }) {
  const [availableParivars, setAvailableParivars] = useState([]);
  const [selectedParivar, setSelectedParivar] = useState(null);
  const [ready, setReady] = useState(false);
  const [storedValid, setStoredValid] = useState(false);
  const [explicitChoice, setExplicitChoice] = useState(() => {
    try {
      return localStorage.getItem(PARIVAR_CHOSEN_KEY) === '1';
    } catch (err) {
      return false;
    }
  });

  const persist = useCallback((parivar) => {
    try {
      localStorage.setItem(PARIVAR_STORAGE_KEY, String(parivar.id));
    } catch (err) {
      // Storage unavailable (private mode) — selection just won't persist.
    }
  }, []);

  const readStoredId = useCallback(() => {
    try {
      const raw = Number.parseInt(localStorage.getItem(PARIVAR_STORAGE_KEY), 10);
      return Number.isInteger(raw) && raw > 0 ? raw : null;
    } catch (err) {
      return null;
    }
  }, []);

  const pickSelection = useCallback((list, storedId) => {
    const stored = storedId && list.find((p) => p.id === storedId);
    if (stored) return stored;
    const sarkhej = list.find((p) => p.slug === DEFAULT_PARIVAR_SLUG);
    if (sarkhej) return sarkhej;
    return list[0] || null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const storedId = readStoredId();

    parivarApi
      .list()
      .then((data) => {
        if (cancelled) return;
        const list = data.results || (Array.isArray(data) ? data : []);
        setAvailableParivars(list);
        // Capture whether the stored id still exists among the active Parivars
        // BEFORE the fallback persist overwrites it with the default — that
        // way a removed/deactivated Parivar still triggers the first-visit
        // prompt instead of silently melting into Sarkhej.
        setStoredValid(storedId != null && list.some((p) => p.id === storedId));
        const chosen = pickSelection(list, storedId);
        if (chosen) {
          setSelectedParivar(chosen);
          persist(chosen);
        }
      })
      .catch(() => {
        // Backend unreachable — the selector still shows the default and the
        // request is retried on the next full page load.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [persist, pickSelection, readStoredId]);

  const changeParivar = useCallback(
    (parivar) => {
      const target =
        parivar && typeof parivar === 'object'
          ? parivar
          : availableParivars.find((p) => p.id === Number(parivar)) || null;
      if (!target) return;
      setSelectedParivar(target);
      persist(target);
      // Any explicit pick (navbar dropdown or first-visit modal save) counts
      // as the visitor's considered choice — once set, we stop prompting.
      setExplicitChoice(true);
      try {
        localStorage.setItem(PARIVAR_CHOSEN_KEY, '1');
      } catch (err) {
        // Storage unavailable — the flag just won't persist.
      }
    },
    [availableParivars, persist]
  );

  const markExplicitChoice = useCallback(() => {
    setExplicitChoice(true);
    try {
      localStorage.setItem(PARIVAR_CHOSEN_KEY, '1');
    } catch (err) {
      // Storage unavailable — the flag just won't persist.
    }
  }, []);

  // A prompt is required only when the visitor has no considered Parivar
  // choice on record, or the one they had was removed/deactivated since.
  const needsOnboarding = useMemo(
    () => ready && (!explicitChoice || !storedValid),
    [ready, explicitChoice, storedValid]
  );

  const value = useMemo(
    () => ({
      availableParivars,
      selectedParivar,
      ready,
      needsOnboarding,
      changeParivar,
      markExplicitChoice,
    }),
    [availableParivars, selectedParivar, ready, needsOnboarding, changeParivar, markExplicitChoice]
  );

  return <ParivarContext.Provider value={value}>{children}</ParivarContext.Provider>;
}

export function useParivar() {
  return useContext(ParivarContext);
}

export default ParivarContext;